import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import FormatCurrency from 'components/Common/FormatCurrency'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import CruisePromotionPriceUnit from 'components/Cruises/Common/CruisePromotionPriceUnit'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import type { CruiseOnlyOfferView } from 'hooks/Cruise/useCruiseOfferView'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import useCruiseSelectedCabinType from 'hooks/useCruiseSelectedCabinType'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
interface Props {
  view: CruiseOnlyOfferView
}

function CruiseSearchTilePricing({ view }: Props) {
  const intl = useIntl()
  const {
    lowestPrice,
    memberLowestPrice,
    memberPriceType,
    discountPills,
  } = view

  const selectedCabinType = useCruiseSelectedCabinType(view.cruiseType)
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const promotionalPrice = lowestPrice.promotionalPrice

  const showModal = useContext(ModalContext)

  const openDiscountTermsAndConditionsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  const twinShareLabel = intl.formatMessage({
    id: 'Zez7WNfVlG415oswa9hq',
    defaultMessage: 'Twin Share',
    description: 'Cruise cabin pricing basis label in the cruise offer or booking section',
  })

  let priceCaption: string
  if (promotionalPrice) {
    priceCaption = cabinTypeLabels[view.cabinCategory] ?? view.cabinCategory
  } else if (selectedCabinType) {
    priceCaption = intl.formatMessage(
      {
        id: 'Cslp2c34XbFUSRznteOE',
        defaultMessage: '{cabinType} from',
        description: 'Cruise search tile price caption with cabin type in the cruises section',
      },
      { cabinType: cabinTypeLabels[selectedCabinType] ?? selectedCabinType },
    )
  } else {
    priceCaption = intl.formatMessage({
      id: 'SyeN89mAiH2FxiXDKqiw',
      defaultMessage: 'From',
      description: 'Cruise search tile price caption without cabin type',
    })
  }

  return <Group direction="vertical" gap={8}>
    <div>
      {promotionalPrice && <>
        <PriceRowPriceCaption>{priceCaption}</PriceRowPriceCaption>
        <Group direction="horizontal" gap={4} verticalAlign="end">
          <LuxPlusPriceStack size="L" price={promotionalPrice.total} saleUnit={promotionalPrice.type} />
          <PriceRowCaption><CruisePromotionPriceUnit promotion={promotionalPrice.promotion} /></PriceRowCaption>
        </Group>
        {promotionalPrice.perPersonPrice !== undefined && <Caption variant="medium" weight="bold" colour="success-dark">
          <FormattedMessage
            id="UDli10X2x9NsPnjIDvB0"
            defaultMessage="{price}/person"
            description="Per-person equivalent shown under a fixed cruise promotional total price"
            values={{ price: <FormatCurrency value={promotionalPrice.perPersonPrice} /> }}
          />
        </Caption>}
      </>}
      {!promotionalPrice && <>
        <PriceRowPriceCaption>{priceCaption}</PriceRowPriceCaption>
        <Group direction="horizontal" gap={4} verticalAlign="end">
          <LuxPlusPriceStack
            size="L"
            price={lowestPrice.primaryPrice.total}
            memberPrice={memberLowestPrice?.primaryPrice.total}
            saleUnit={lowestPrice.primaryPrice.type}
            mode={memberPriceType === 'STANDARD_PRICE' ? 'member-only' : undefined}
          />
        </Group>
        {lowestPrice.secondaryPrice && <PriceRowCaption>
          <FormattedMessage
            id="tS3bNBvwd_j1ZcVYvSzQ"
            defaultMessage="{price} /{priceType} · {twinShare}"
            description="Cruise search tile secondary price with type and twin share label"
            values={{
              price: <FormatCurrency value={lowestPrice.secondaryPrice.total} />,
              priceType: lowestPrice.secondaryPrice.type,
              twinShare: twinShareLabel,
            }}
          />
        </PriceRowCaption>}
      </>}
      {!lowestPrice.secondaryPrice && discountPills?.price && discountPills.discountPercentage && <PriceRowValueDiscountWithCaption
        size="M"
        originalValue={discountPills.price}
        discountPercentage={discountPills.discountPercentage}
        onInfoIconClick={openDiscountTermsAndConditionsModal}
      />}
    </div>
    <div>
      {!promotionalPrice && !lowestPrice.secondaryPrice && <PriceRowCaption>{twinShareLabel}</PriceRowCaption>}
      <PriceRowTaxesAndFees />
      <PriceRowAgentHubCommission
        offerId={view.offer.id}
        offer={view.offer}
        productType={view.offer.productType}
      />
    </div>
  </Group>
}

export default CruiseSearchTilePricing
